<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            isAdd
              ? `${$t("actions.add")} ${$tc("user_group_selection_option.title", 1)}`
              : `${$t("actions.edit")} ${$tc("user_group_selection_option.title", 1)}`
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <validation-provider
            #default="validationContext"
            rules="required"
            :name="$t('user_group_selection_option.user')"
          >
            <b-form-group
              class="text-capitalize"
              :label="$tc('user_group_selection_option.user', 2)"
              label-for="users"
              :state="getValidationState(validationContext)"
            >
              <base-select-pagination
                url="/v1/users"
                input-id="id_user"
                v-model="itemData.user"
                :option-mapper="userMapperOption"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            rules="required"
            :name="$t('user_group_selection_option.groups')"
          >
            <b-form-group
              class="text-capitalize"
              :label="$tc('user_group_selection_option.groups', 2)"
              label-for="users"
              :state="getValidationState(validationContext)"
            >
              <v-select
                input-id="id_groups"
                v-model="itemData.id_groups"
                :options="groupOptions"
                :reduce="(val) => val.value"
                multiple
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import { ref, onMounted, computed, watch } from "@vue/composition-api";
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { required, alphaNum, email, positive, required_if } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { getResponseErrors } from "@/utils/responseErrors";
import UsersSelectPaginated from "@/components/UsersSelectPaginated.vue";
import axios from "@/libs/axios";
import BaseSelectPagination from "@/components/Form/BaseSelectPagination.vue";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
    UsersSelectPaginated,
    vSelect,
    BaseSelectPagination,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {
    onMounted(() => {
      store.dispatch("group/getAll");
    });

    const blankData = {
      user: null,
      id_user: null,
      id_groups: [],
    };

    const toast = useToast();
    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const onSubmit = async () => {
      const formData = {
        id_user: itemData.value.user.id,
        id_groups: itemData.value.id_groups,
      };

      const dispatch = store.dispatch("user_group_selection_option/add", formData);

      dispatch
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          const { message, strValidationErrors } = getResponseErrors(response);

          toast({
            component: ToastificationContent,
            props: {
              title: strValidationErrors ? strValidationErrors : message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

    const userMapperOption = (item) => ({
      id: item.id,
      label: `${item.full_name ?? ""} | ${item.email ?? ""}`,
    });

    const loadData = () => {
      if (!props.isAdd) {
        itemData.value = {
          user: userMapperOption(props.itemEdit),
          id_groups: props.itemEdit.group_selection_options?.map((option) => option.id),
        };
      }
    };

    const groupOptions = computed(() => store.getters["group/getSelectedItemsGroup"]);

    return {
      itemData,
      onSubmit,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      groupOptions,
      userMapperOption,
    };
  },
};
</script>

